import { Editor, Transforms } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { Path } from 'slate'

/**
 * Remove a top-level block and leave the selection somewhere that still exists.
 *
 * Slate keeps the old selection after `removeNodes`, so removing a void block (image, audio)
 * leaves an anchor pointing at a dead path and the editor blurs or throws on the next render.
 * Discarding a sketch is the gesture we tell users is safe, so it must not be the gesture that
 * takes the editor down.
 *
 * Where the caret lands, in order of preference: the start of the block that moved up into the
 * removed block's place, then the end of the preceding block, then a fresh empty paragraph when
 * the document would otherwise have no nodes left.
 */
export const removeBlockAndRepairSelection = (editor: TunaEditor, at: Path) => {
  Editor.withoutNormalizing(editor, () => {
    Transforms.removeNodes(editor, { at })

    if (editor.children.length === 0) {
      Transforms.insertNodes(editor, { type: 'p', children: [{ text: '' }] } as never, { at: [0] })
      Transforms.select(editor, Editor.start(editor, [0]))
      return
    }

    const removedIndex = at[0]
    if (removedIndex < editor.children.length) {
      Transforms.select(editor, Editor.start(editor, [removedIndex]))
      return
    }

    Transforms.select(editor, Editor.end(editor, [editor.children.length - 1]))
  })
}
